import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { SL_GATE } from 'Defaults/modals'
import { checkIsArchive, isNilEmpty } from 'Utils'
import useRequiresTermsOfUseAgreement from 'Hooks/useCheckTermsOfUse'
import { checkListingRequiresEmailVerification, checkListingLocked } from 'Utils/listingUtils'
import useListingOpenLoginSignup from 'Hooks/useListingOpenLoginSignup'
import { createPhotoPaths } from 'Utils/photoUtils'
import { Locked, Unlocked } from './components'
import { _ListingPreview } from './styles'

const GA_SOURCE = 'Listing Card'


const ListingPreview = (
  {
    searchPage, offer, setTargetComponent,
    id, sourcePage, openEmailVerifyModal, status,
    title, subtitle, url, askingPrice, userFavs,
    userIsLoggedIn, bathrooms, parkingSpots,
    toggleFav, gridNumber, photoBaseUrl, photoCount, mlsNumber,
    onHover, onMouseLeave, mobileBrowser, listing,
    createdAt, sqftMin, sqftMax, sqft, tourUrl,
    slogan, maintenanceFee, bannerId, closedPrice, endDateUnix,
    showSlider = true, unitNumber, bedType, propertyType, cssGridStyle, noMarginBox, userScrolled = true,
    openNewTab = false, brokerage, photoVersion, sqftSource, entryDateUnix,
    userEmailVerified, dataSource, gaCategory,
    isNearbyListing = false, HiddenCompareCta = false, hasMyAgent,
    userIsOneOfAgentAdminSupport, isMobileApp,
    handleClick, openEditFavouritesModal, isFavouritesPage, homeGridNumber, hideImageCount,
    estimateListing, showEstimate, googleAnalyticsEvent, photoSource,
  },
) => {
  const [userIsLoggedInClientSide, setIsLoggedInClientSide] = useState(false)

  useEffect(() => {
    setIsLoggedInClientSide(userIsLoggedIn)
  }, [userIsLoggedIn])

  const isArchived = checkIsArchive(status)
  const {
    requireTermsConditionAgreement,
    termsConditionRequiredId,
    openTermsConditionModal,
    requireTermsEmailVerification,
  } = useRequiresTermsOfUseAgreement({
    listingSource: listing?.source,
  })

  const { openLogin, openSignup } = useListingOpenLoginSignup({
    termsConditionRequiredId,
    sourcePage,
  })

  const onListingClick = (e, listingObj = {}) => {
    const openHouseExist = !isNilEmpty(listingObj?.openHouses)
    googleAnalyticsEvent({
      category: sourcePage,
      action: `[For ${offer}]${listing?.featured ? '[Featured]' : ''}${openHouseExist ? '[Open House]' : ''}[Select]`,
    })
    if(!searchPage && !openNewTab && !e.metaKey && !e.ctrlKey && !isMobileApp) {
      setTargetComponent({
        component: 'Listing',
        id: listingObj.id,
      })
    }

    if(handleClick) {
      handleClick(listing)
    }
  }
  const onSignupRequiredHandler = (
    {
      gate,
    }) => {
    openSignup({
      gate,
      listing,
      termsConditionRequiredId,
    })
  }

  const openLoginHandler = (e) => {
    e.preventDefault()
    e.stopPropagation()
    openLogin({
      gate: SL_GATE.LISTING_CARD_INFO,
      listing,
      termsConditionRequiredId,
    })
  }

  const firstPhotoObj = createPhotoPaths({
    photoBaseUrl,
    mlsNumber,
    photoIndex: 1,
    photoVersion,
    photoSource,
  })

  const imgUrl = mlsNumber ? firstPhotoObj.imgSmall : ''
  const lowResImgUrl = mlsNumber ? firstPhotoObj.lowQualityImgPath : ''

  const isSold = status === 'Sold'


  const userFavListings = userFavs && { ...userFavs.listings }
  const favourited = userFavListings ? mlsNumber in userFavListings : null
  const building = listing && listing.building && listing.building.name
  const featured = !!(listing && listing.featured)
  const hiddenFields = userIsOneOfAgentAdminSupport ? null : listing.hidden_fields

  const requiredEmailVerification = checkListingRequiresEmailVerification(
    {
      userIsAuthentificated: userIsLoggedInClientSide,
      isArchived,
      userEmailVerified,
      requireTermsEmailVerification,
    },
  )
  const showLockedStyled = checkListingLocked({
    userIsAuthentificated: userIsLoggedInClientSide,
    listing,
    isArchived,
    requiredEmailVerification,
    requireTermsConditionAgreement,
    termsConditionRequiredId,
  })
  const listingInner = (showLockedStyled) ?
    (
      <Locked
        hiddenFields={hiddenFields}
        id={id}
        userIsLoggedIn={userIsLoggedInClientSide}
        closedPrice={closedPrice}
        isArchived={isArchived}
        title={title}
        subtitle={subtitle}
        imageSrc={imgUrl}
        url={url}
        onLogin={openLoginHandler}
        onRegister={onSignupRequiredHandler}
        onTermsConditionClick={openTermsConditionModal}
        onHover={onHover}
        onMouseLeave={onMouseLeave}
        onListingClick={onListingClick}
        mobileBrowser={mobileBrowser}
        lowResImgUrl={lowResImgUrl}
        askingPrice={askingPrice}
        source={listing?.source}
        offer={offer}
        status={status}
        isSold={isSold}
        slogan={slogan}
        building={building}
        unitNumber={unitNumber}
        userScrolled={userScrolled}
        openNewTab={openNewTab}
        entryDateUnix={entryDateUnix}
        userEmailVerified={userEmailVerified}
        openEmailVerifyModal={openEmailVerifyModal}
        requiredEmailVerification={requiredEmailVerification}
        requireTermsConditionAgreement={requireTermsConditionAgreement}
      />
    ) : (
      <Unlocked
        id={id}
        title={title}
        url={url}
        favourited={favourited}
        askingPrice={askingPrice}
        bedType={bedType}
        featured={featured}
        propertyType={propertyType}
        bathrooms={bathrooms}
        parkingSpots={parkingSpots}
        userIsLoggedIn={userIsLoggedInClientSide}
        onLogin={openLoginHandler}
        onRegister={onSignupRequiredHandler}
        favouriteClick={toggleFav}
        onHover={onHover}
        onMouseLeave={onMouseLeave}
        onListingClick={onListingClick}
        photoBaseUrl={photoBaseUrl}
        photoCount={photoCount}
        mlsNumber={mlsNumber}
        mobileBrowser={mobileBrowser}
        createdAt={createdAt}
        entryDateUnix={entryDateUnix}
        sqftMin={sqftMin}
        sqftMax={sqftMax}
        sqft={sqft}
        status={status}
        isSold={isSold}
        offer={offer}
        slogan={slogan}
        building={building}
        showSlider={showSlider}
        unitNumber={unitNumber}
        maintenanceFee={Number(maintenanceFee)}
        userScrolled={userScrolled}
        openNewTab={openNewTab}
        bannerId={bannerId}
        brokerage={brokerage}
        isArchived={isArchived}
        isNearbyListing={isNearbyListing}
        closedPrice={closedPrice}
        endDateUnix={endDateUnix}
        photoVersion={photoVersion}
        sqftSource={sqftSource}
        dataSource={dataSource}
        gaCategory={gaCategory}
        listing={listing}
        sourcePage={sourcePage}
        HiddenCompareCta={HiddenCompareCta}
        hiddenFields={hiddenFields}
        hasMyAgent={hasMyAgent}
        tourUrl={tourUrl}
        isMobileApp={isMobileApp}
        openEditFavouritesModal={openEditFavouritesModal}
        isFavouritesPage={isFavouritesPage}
        gaSource={GA_SOURCE}
        hideImageCount={hideImageCount}
        estimateListing={estimateListing}
        showEstimate={showEstimate}
      />
    )
  if(cssGridStyle) {
    return (
      <div style={{ display: 'inline-block' }}>
        {listingInner}
      </div>
    )
  }
  return (
    <_ListingPreview
      xs={gridNumber || 12}
      sm={gridNumber || 6}
      md={gridNumber || homeGridNumber || 4}
      lg={gridNumber || homeGridNumber || 3}
      noMargin={noMarginBox ? 1 : 0}
      data-test-id="listingPreview"
    >
      {listingInner}
    </_ListingPreview>
  )
}

const favouriteObjectPropType = PropTypes.shape({
  note: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.string),
})

ListingPreview.propTypes = {
  listing: PropTypes.shape({
    building: PropTypes.shape({
      name: PropTypes.string,
    }),
    source: PropTypes.string,
    featured: PropTypes.number,
    hidden_fields: PropTypes.shape({
      photos: PropTypes.bool,
      listing_info: PropTypes.bool,
      prices: PropTypes.bool,
    }),
    advertise_permission: PropTypes.number,
  }),
  id: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  url: PropTypes.string.isRequired,
  userIsLoggedIn: PropTypes.bool.isRequired,
  askingPrice: PropTypes.number.isRequired,
  bathrooms: PropTypes.number,
  parkingSpots: PropTypes.number,
  toggleFav: PropTypes.func.isRequired,
  gridNumber: PropTypes.number,
  showSlider: PropTypes.bool,
  maintenanceFee: PropTypes.number,
  photoBaseUrl: PropTypes.string.isRequired,
  mlsNumber: PropTypes.string.isRequired,
  onHover: PropTypes.func,
  onMouseLeave: PropTypes.func,
  offer: PropTypes.string,
  searchPage: PropTypes.bool,
  mobileBrowser: PropTypes.bool,
  photoCount: PropTypes.number,
  unitNumber: PropTypes.number,
  createdAt: PropTypes.string,
  sqftMin: PropTypes.number,
  sqftMax: PropTypes.number,
  sqft: PropTypes.number,
  slogan: PropTypes.string,
  setTargetComponent: PropTypes.func,
  tourUrl: PropTypes.string,
  handleClick: PropTypes.func,
  status: PropTypes.string,
  userFavs: PropTypes.shape({
    listings: PropTypes.objectOf(favouriteObjectPropType),
  }),
  bedType: PropTypes.string,
  // bedrooms: PropTypes.number,
  propertyType: PropTypes.string,
  cssGridStyle: PropTypes.bool,
  noMarginBox: PropTypes.bool,
  userScrolled: PropTypes.bool,
  openNewTab: PropTypes.bool,
  bannerId: PropTypes.number,
  brokerage: PropTypes.string,
  dataSource: PropTypes.string,
  gaCategory: PropTypes.string,
  sourcePage: PropTypes.string,
  isNearbyListing: PropTypes.bool,
  closedPrice: PropTypes.number,
  endDateUnix: PropTypes.number,
  photoVersion: PropTypes.number,
  entryDateUnix: PropTypes.number,
  sqftSource: PropTypes.string,
  HiddenCompareCta: PropTypes.bool,
  userEmailVerified: PropTypes.number,
  hasMyAgent: PropTypes.bool,
  userIsOneOfAgentAdminSupport: PropTypes.bool,
  openEmailVerifyModal: PropTypes.func,
  isMobileApp: PropTypes.bool,
  openEditFavouritesModal: PropTypes.func,
  isFavouritesPage: PropTypes.bool,
  homeGridNumber: PropTypes.number,
  hideImageCount: PropTypes.bool,
  showEstimate: PropTypes.bool,
  estimateListing: PropTypes.shape({}),
  googleAnalyticsEvent: PropTypes.func,
  photoSource: PropTypes.string,
}

export default ListingPreview
